import { yupResolver } from "@hookform/resolvers/yup";
import { DateTime } from "luxon";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from 'yup';
import { updateUserDetails } from "../../api/user";
import { useAuth } from "../../auth/AuthContext";
import { Button } from "../../components/Buttons";
import { SmartInput } from "../../components/SmartForm";
import { SmartRadioGroup } from "../../components/SmartForm/SmartRadioGroup";
import { toastNotification } from "../../components/ToastNotification";
import { GENDER } from "../../models/User";

interface UpdateProfileFormInputs {
    firstname: string;
    lastname: string;
    email: string;
    day: number;
    month: number;
    year: number;
    phone: string;
    gender: number;
}

const schema = yup.object({
    firstname: yup.string().required('First name is required'),
    lastname: yup.string().required('Last name is required'),
    email: yup.string().email('Please enter a valid email address').required('Email address is required'),
    day: yup.number().lessThan(32, 'Please enter a valid date').required('Date is required'),
    month: yup.number().lessThan(13, 'Please enter a valid month').required('Month is required'),
    year: yup.number().lessThan(DateTime.now().get('year'), 'Please enter a valid year in the past').required('Year is required'),
    phone: yup.string().matches(/^[0-9]*$/g, 'Please enter a valid phone number').required('Phone is required'),
    gender: yup.number().required('Gender is required')
});

const genderOptions = [
    {
        label: 'Male',
        value: '1',
        description: ''
    },
    {
        label: 'Female',
        value: '2',
        description: ''
    }
];

export const UpdateProfileForm: React.FC = () => {
    const { user, updateUser } = useAuth();
    const [gender, setGender] = useState<GENDER>(user?.gender ?? GENDER.MALE);

    const { register, handleSubmit, setValue, formState: { errors }, reset } = useForm<UpdateProfileFormInputs>({
        resolver: yupResolver(schema),
        defaultValues: {
            firstname: user?.firstname,
            lastname: user?.lastname,
            email: user?.email,
            phone: user?.phone,
            gender: user?.gender,
            day: new Date(user?.dob ?? '').getDate(),
            month: new Date(user?.dob ?? '').getMonth() + 1,
            year: new Date(user?.dob ?? '').getFullYear(),
        }
    });

    const onSubmit = async (values: UpdateProfileFormInputs) => {
        const { day, month, year } = values;

        const dob = DateTime.now().set({ day, month, year }).toJSDate().toISOString();

        const result = await updateUserDetails(user?._id ?? '', { ...values, dob });

        if(!result.isError) {
            updateUser(result.content);
            toastNotification({ variant: 'success', title: 'Profile updated', message: `Your profile has been successfully updated` });
        }
    };

    const handleGenderChange = (val: GENDER) => {
        setGender(val);
        setValue('gender', val);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="md:grid md:grid-cols-2 md:gap-4">
                <div className="mb-4">
                    <SmartInput register={register('firstname')} placeholder='First name' label='First name' error={errors.firstname?.message} />
                </div>
                <div className="mb-4">
                    <SmartInput register={register('lastname')} placeholder='Last name' label='Last name' error={errors.lastname?.message} />
                </div>
            </div>
            <div className="md:grid md:grid-cols-2 md:gap-4">
                <div className="mb-4">
                    <SmartInput register={register('email')} placeholder='Email address' label='Email address' error={errors.email?.message} />
                </div>
                <div className="mb-4">
                    <SmartInput register={register('phone')} autoComplete="false" placeholder="Phone number" label="Phone number" error={errors.phone?.message} />
                </div>
            </div>
            <label className="block text-sm font-medium text-black mb-1">Gender</label>
            <SmartRadioGroup options={genderOptions} value={gender.toString()} onChange={(val: any) => handleGenderChange(val as GENDER)} />
            <label className="block text-sm font-medium text-black mb-1">Date of birth (DD/MM/YYYY)</label>
            <div className="flex items-end mb-4">
                <div className="mr-2 w-16">
                    <SmartInput type='number' register={register('day')} autoComplete="false" placeholder="DD" error={errors.day?.message} />
                </div>
                <div className="mr-2 w-16">
                    <SmartInput type='number' register={register('month')} autoComplete="false" placeholder="MM" error={errors.month?.message} />
                </div>
                <div className="mr-2 w-24">
                    <SmartInput type='number' register={register('year')} autoComplete="false" placeholder="YYYY" error={errors.year?.message} />
                </div>
            </div>
            <div className="grid gap-4 mt-8 sm:grid-cols-2">
                <Button variant='gray' type='button' onClick={() => reset()}>Reset</Button>
                <Button variant='primary' type='submit'>Update profile</Button>
            </div>
        </form>
    );
};